// src/resources/users.js
import ResponsiveDatagrid from '../components/ResponsiveDatagrid';
import React from 'react';
import {
  List,
  TextField,
  EmailField,
  BooleanField,
  DateField,
  EditButton,
  Edit,
  TextInput,
  BooleanInput,
  ChipField,
  TopToolbar,
  CreateButton,
  ExportButton,
  FilterButton,
  SelectInput,
  SearchInput,
  TabbedForm,
  FormTab,
  ReferenceManyField,
  Datagrid,
  ShowButton,
  Button,
  useNotify,
  useRefresh,
  useRecordContext,
  useUpdate,
  FormDataConsumer, 
  useDataProvider,
  FunctionField,
  Pagination,
} from 'react-admin';
import { UserImportButton } from './UserImport';
import { Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Select, MenuItem, FormControl, InputLabel } from '@mui/material'; // <-- تم استيراد مكونات Dialog
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import StarIcon from '@mui/icons-material/Star';
import { useState } from 'react';

// --- User Filters ---
const userFilters = [
    <SearchInput source="q" alwaysOn />, // Use SearchInput for better UX
    <SelectInput source="userType" choices={[
        { id: 'DRIVER', name: 'Driver' },
        { id: 'PASSENGER', name: 'Passenger' },
    ]} />,
    <SelectInput source="isVerified" label="Verified ID" choices={[
        { id: true, name: 'Yes' },
        { id: false, name: 'No' },
    ]} />,
     <SelectInput source="isPremium" label="Premium" choices={[
        { id: true, name: 'Yes' },
        { id: false, name: 'No' },
    ]} />,
];

// --- User List Actions (Added Filters Button) ---
const UserListActions = () => (
    <TopToolbar>
        <FilterButton /> {/* Add Filter button */}
        <CreateButton />
        <ExportButton />
        <UserImportButton />
    </TopToolbar>
);

// --- User Title for Edit Page ---
const UserTitle = () => {
  const record = useRecordContext();
  return <span>Editing User: {record ? `"${record.name}" (${record.id})` : ''}</span>;
};


// --- User List Component (Improved) ---
export const UserList = (props) => {
  return (
    <List {...props} actions={<UserListActions />} filters={userFilters} title="All Users">
      <ResponsiveDatagrid
        rowClick="edit"
        primaryText={record => record.name}
        secondaryText={record => record.email}
        tertiaryText={record => record.phone}
      >
        <TextField source="name" />
        <EmailField source="email" />
        <TextField source="phone" />
        <ChipField source="userType" label="Type" emptyText="N/A" /> {/* Added User Type */}
        <BooleanField source="isVerified" label="Verified ID" /> {/* Use BooleanField (shows check/cross) */}
        <BooleanField source="isPremium" label="Premium" /> {/* Use BooleanField */}
        <DateField source="createdAt" label="Join Date" />
        <EditButton />
      </ResponsiveDatagrid>
    </List>
  );
};

// --- =================================================== ---
// ---                 **الكود الجديد بالكامل** ---
// --- =================================================== ---

/**
 * زر احترافي لفتح نافذة منبثقة ومنح اشتراك بريميوم.
 */
const GrantPremiumButton = () => {
    const record = useRecordContext();
    const notify = useNotify();
    const refresh = useRefresh();
    const dataProvider = useDataProvider(); 
    const [isLoading, setIsLoading] = useState(false);

    const handleGrant = () => {
        setIsLoading(true);
        // dataProvider.create يرسل طلب POST
        // وهو مطابق للمسار في `adminRoutes.js`
        dataProvider.create('debug/grant-premium', { 
            data: { userId: record.id } 
        })
            .then(() => {
                notify('Premium granted for 1 month.', { type: 'success' });
                setIsLoading(false);
                refresh(); 
            })
            .catch((error) => {
                notify(`Error granting premium: ${error.body?.error || error.message}`, { type: 'warning' });
                setIsLoading(false);
            });
    };

    if (!record || record.isPremium) return null; 

    return <Button label="Grant 1 Month Premium" onClick={handleGrant} disabled={isLoading} startIcon={<StarIcon />} sx={{ ml: 2, backgroundColor: 'success.light' }} />;
};

/**
 * زر احترافي لفتح نافذة منبثقة وتطبيق الحظر (أو رفعه).
 * @param {Object} props
 * @param {string} props.userId - (اختياري) ID المستخدم لحظر مستخدم غير موجود في useRecordContext (مثل شاشة البلاغات).
 */
export const BanUserButton = ({ userId }) => {
    const record = useRecordContext();
    const notify = useNotify();
    const refresh = useRefresh();
    const dataProvider = useDataProvider();
    const [isLoading, setIsLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [duration, setDuration] = useState(7); // المدة الافتراضية 7 أيام

    const targetUserId = userId || record?.id; // استخدم ID الممرر أو ID من السياق
    if (!targetUserId) return null;

    // التحقق من حالة الحظر الحالية للمستخدم (من السيرفر)
    // السيرفر يرسل `null` للحظر الدائم، تاريخ في المستقبل للحظر المؤقت، تاريخ في الماضي لغير المحظور
    const banExpiresAt = record?.banExpiresAt;
    let isBanned = false;
    if (banExpiresAt === null) {
        isBanned = true; // حظر دائم
    } else if (banExpiresAt) {
        isBanned = new Date(banExpiresAt) > new Date(); // حظر مؤقت
    }

    const handleClickOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const handleBan = () => {
        setIsLoading(true);
        
        // `duration` هو القيمة من SelectInput (e.g., 7, 30, 0, null)
        const payload = { 
            data: { banDurationDays: duration } 
        };
        
        // نستخدم `dataProvider.create` الذي يرسل طلب POST
        // إلى المسار `users/:id/ban` وهو مطابق تمامًا للسيرفر
        dataProvider.create(`users/${targetUserId}/ban`, payload)
            .then(() => {
                let message = 'User ban status updated.';
                if (duration === 0) message = 'User unbanned.';
                else if (duration === null) message = 'User permanently banned.';
                else message = `User banned for ${duration} day(s).`;
                
                notify(message, { type: 'success' });
                setIsLoading(false);
                handleClose();
                refresh();
            })
            .catch((error) => {
                notify(`Error: ${error.body?.error || error.message}`, { type: 'warning' });
                setIsLoading(false);
            });
    };

    return (
        <>
            <Button 
                label={isBanned ? "Update Ban" : "Ban User"} 
                onClick={handleClickOpen} 
                disabled={isLoading} 
                color={isBanned ? "secondary" : "warning"} 
                startIcon={<PersonRemoveIcon />} 
                sx={{ ml: 2 }} 
            />
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
                <DialogTitle>{isBanned ? "Update Ban Status" : "Ban User"}</DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        Select the ban duration for this user.
                    </Typography>
                    <FormControl fullWidth margin="dense" sx={{ mt: 2 }}>
                        <InputLabel id="ban-duration-label">Ban Duration</InputLabel>
                        <Select
                            labelId="ban-duration-label"
                            value={duration}
                            label="Ban Duration"
                            onChange={(e) => setDuration(e.target.value)}
                        >
                            <MenuItem value={1}>1 Day</MenuItem>
                            <MenuItem value={7}>7 Days</MenuItem>
                            <MenuItem value={30}>30 Days</MenuItem>
                            <MenuItem value={null}>Permanent</MenuItem>
                            <MenuItem value={0} sx={{ color: 'success.main', fontWeight: 'bold' }}>
                                Lift Ban (Unban)
                            </MenuItem>
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} label="Cancel" />
                    <Button onClick={handleBan} label="Confirm" color="warning" disabled={isLoading} />
                </DialogActions>
            </Dialog>
        </>
    );
};

// --- =================================================== ---
// ---                نهاية الكود الجديد                    ---
// --- =================================================== ---


// --- User Edit Component (Improved with Tabs) ---
export const UserEdit = (props) => (
  <Edit title={<UserTitle />} {...props}>
    <TabbedForm>
        {/* Tab 1: Basic Info (كما هو) */}
        <FormTab label="Summary">
            <TextInput source="id" disabled fullWidth/>
            <TextInput source="name" fullWidth/>
            <TextInput source="email" fullWidth/>
            <TextInput source="phone" fullWidth/>
            <TextInput source="city" fullWidth/>
            <SelectInput source="userType" choices={[
                { id: 'DRIVER', name: 'Driver' },
                { id: 'PASSENGER', name: 'Passenger' },
            ]} fullWidth/>
            <DateField source="createdAt" label="Join Date" fullWidth/>
             <FormDataConsumer>
                {({ formData, ...rest }) => formData.idVerificationStatus === 'REJECTED' && formData.idRejectionReason && (
                     <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'warning.main', borderRadius: 1 }}>
                        <Typography variant="subtitle2" color="warning.main">ID Verification Rejected:</Typography>
                        <Typography variant="body2">{formData.idRejectionReason}</Typography>
                    </Box>
                )}
            </FormDataConsumer>
        </FormTab>

        {/* Tab 2: Status & Actions (كما هو) */}
         <FormTab label="Status & Actions">
            <BooleanInput source="isVerified" label="ID is Verified?" />
            <BooleanInput source="isPremium" label="Has Premium Subscription?" />
             <DateField source="premiumEndDate" label="Premium Ends On" showTime={false}/>
            <BooleanInput source="hasUsedFreeTrial" label="Has Used Free Trial?" />
            <FormDataConsumer>
                 {({ formData, ...rest }) => {
                    let banStatus = "Not Banned";
                    let banColor = "success.main";
                    if (formData.banExpiresAt === null) {
                        banStatus = "Banned Permanently";
                        banColor = "error.main";
                    } else if (formData.banExpiresAt && new Date(formData.banExpiresAt) > new Date()) {
                        banStatus = `Banned until ${new Date(formData.banExpiresAt).toLocaleDateString()}`;
                        banColor = "warning.main";
                    }
                    return (
                        <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'grey.400', borderRadius: 1 }}>
                            <Typography variant="h6" color={banColor}>Ban Status: {banStatus}</Typography>
                        </Box>
                    );
                 }}
            </FormDataConsumer>
             <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-start', flexWrap: 'wrap' }}>
                 <Typography variant="h6" sx={{ mr: 2, alignSelf: 'center' }}>Actions:</Typography>
                 <GrantPremiumButton />
                 <BanUserButton />
            </Box>
        </FormTab>

        {/* Tab 3: Car Details (كما هو) */}
        <FormTab label="Car">
             <FormDataConsumer>
                {({ formData, ...rest }) => formData.car ? (
                    <>
                        <Typography variant="h6" gutterBottom>Car Details</Typography>
                        <TextField source="car.brand" />
                        <TextField source="car.model" />
                        <TextField source="car.year" />
                        <TextField source="car.plate" />
                        <BooleanField source="car.isVerified" label="Car Verified" />
                         <ChipField source="car.verificationStatus" label="Car Status" />
                        {formData.car.verificationStatus === 'REJECTED' && formData.car.rejectionReason && (
                            <Box sx={{ mt: 1, p: 1, border: '1px solid', borderColor: 'warning.main', borderRadius: 1 }}>
                                <Typography variant="caption" color="warning.main">Car Rejection Reason:</Typography>
                                <Typography variant="body2" sx={{fontSize: '0.8rem'}}>{formData.car.rejectionReason}</Typography>
                            </Box>
                        )}
                    </>
                ) : (
                    <Typography>No car associated with this user.</Typography>
                )}
            </FormDataConsumer>
        </FormTab>

        {/* Tab 4: Rides (كما هو) */}
         <FormTab label="Rides">
             <ReferenceManyField label="Rides as Driver" reference="rides" target="driverId">
                <Datagrid>
                    <TextField source="origin" />
                    <TextField source="destination" />
                    <DateField source="time" showTime />
                    <ChipField source="status" />
                    <ShowButton />
                </Datagrid>
            </ReferenceManyField>
         </FormTab>

        {/* --- [بداية التعديل] Tab 5: Chats --- */}
        <FormTab label="User Chats">
            {/* ReferenceManyField يقوم بجلب البيانات من مورد "chats"
                ويمرر "userId" (id المستخدم الحالي) كفلتر للباك إند
            */}
            <ReferenceManyField 
                label="Conversations" 
                reference="chats" 
                target="userId" // هذا يرسل ?userId=... إلى الباك إند
                pagination={<Pagination />}
            >
                <Datagrid rowClick="show" bulkActionButtons={false}>
                    <TextField source="id" label="Chat ID" />
                    <DateField source="createdAt" showTime label="Created At" />
                    
                    {/* عرض الطرف الآخر في المحادثة (تم تجهيزه في الباك إند) */}
                    <TextField source="other_participants" label="Chat With" style={{ fontWeight: 'bold' }} />
                    
                    {/* عرض مقتطف من آخر رسالة */}
                    <TextField source="last_message_preview" label="Last Message" />
                    
                    <ShowButton label="Open Chat" />
                </Datagrid>
            </ReferenceManyField>
        </FormTab>
        {/* --- [نهاية التعديل] --- */}

    </TabbedForm>
  </Edit>
);